import Vue from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import { v4 as uuid } from 'uuid'

const api = axios.create({ baseURL: process.env.VUE_APP_BASE_URL, timeout: 15000 })
export const sessionState = Vue.observable({ mode: 'unknown', id: '', blocked: false, message: '正在確認登入狀態…' })
export const readToken = () => { try { return JSON.parse(localStorage.getItem('token') || 'null') } catch (_) { return null } }
let credential = ''
let deadline = 0
let checkedAt = 0
let retryAt = 0
let failures = 0
let inFlight = null
let activityPending = null
let activityVersion = 0
let pendingActivityVersion = null
let lastActivitySent = -Infinity
let onClear = () => {}
let finishing = false
let flushing = false
let started = false
const headers = token => ({ Authorization: token, Accept: 'application/json' })
const sameToken = token => readToken()?.access_token === token
const block = () => { sessionState.blocked = true; sessionState.message = '登入狀態確認中，連線恢復後會自動重試。' }
const reset = () => {
  credential = readToken()?.access_token || ''
  sessionState.mode = readToken()?.session_mode || 'unknown'
  sessionState.id = readToken()?.session_id || ''
  sessionState.blocked = !!credential && sessionState.mode !== 'legacy'
  deadline = checkedAt = retryAt = failures = 0
  lastActivitySent = -Infinity
  activityPending = null
  activityVersion = 0
  pendingActivityVersion = null
}
const current = () => {
  if (credential !== (readToken()?.access_token || '')) { const hadToken = !!credential; reset(); if (hadToken && !credential) onClear() }
  return credential
}
const remember = data => {
  sessionState.id = data.session_id
  sessionState.mode = data.session_mode
  const stored = readToken()
  if (stored) localStorage.setItem('token', JSON.stringify({ ...stored, session_id: data.session_id, session_mode: data.session_mode }))
}
export function clearSession (expected = credential) {
  if (expected && !sameToken(expected)) return
  localStorage.removeItem('token')
  reset()
  onClear()
}
// Notification delivery is optional; a confirmed revoked session must still be cleared.
async function claimTimeoutNotice (data, token) {
  if (data.reason !== 'idle_timeout' || !data.notice_claim_code) return false
  try {
    const { data: claim } = await api.post('shared-session/timeout-notice/claim', { notice_claim_code: data.notice_claim_code })
    return claim.show_timeout === true && claim.session_id === data.session_id
  } catch (error) {
    reportSessionError('TIMEOUT_NOTICE_CLAIM_FAILED', null, token, error.response)
    return false
  }
}
async function finish (data, token) {
  block()
  if (finishing || document.visibilityState === 'hidden' || !sameToken(token)) return
  finishing = true
  try {
    const show = await claimTimeoutNotice(data, token)

    if (!sameToken(token)) return
    clearSession(token)
    if (window.location.pathname !== '/home') window.history.replaceState(null, '', '/home')
    if (show) await Swal.fire({ text: '閒置逾時15分鐘\n請重新登入', customClass: { popup: 'idle-timeout-dialog' }, confirmButtonText: '確認', allowOutsideClick: false })
  } finally { finishing = false }
}
function accept (data, token, sentAt) {
  if (!sameToken(token)) return false
  if (!data.session_id || !['legacy', 'shared'].includes(data.session_mode) || !['active', 'expired', 'revoked'].includes(data.state)) throw new Error('INVALID_SESSION_RESPONSE')
  if (sessionState.mode === 'shared' && data.session_mode !== 'shared') throw new Error('SESSION_DOWNGRADE')
  if (sessionState.id && data.session_id !== sessionState.id) throw new Error('SESSION_MISMATCH')
  let nextDeadline = 0
  if (data.session_mode === 'shared') {
    const remaining = Date.parse(data.idle_expires_at) - Date.parse(data.server_now)
    if (!Number.isFinite(remaining)) throw new Error('INVALID_SESSION_RESPONSE')
    // Monotonic browser clock; never trust the workstation wall clock for authorization.
    nextDeadline = sentAt + Math.max(0, remaining)
  }
  // Commit identity only after the complete response has passed validation.
  remember(data)
  deadline = nextDeadline
  checkedAt = performance.now()
  failures = retryAt = 0
  sessionState.blocked = data.state !== 'active' || (data.session_mode === 'shared' && deadline <= performance.now())
  return true
}
function failed (error, token) {
  if (!sameToken(token)) return
  const status = error.response?.status
  if (status === 401) { clearSession(token); return }
  // Shared authorization must be confirmed again after a service failure.
  // Keep the credentials for retry; an outage is not a confirmed idle timeout.
  const invalid = ['INVALID_SESSION_RESPONSE', 'SESSION_DOWNGRADE', 'SESSION_MISMATCH'].includes(error.message)
  if (invalid || sessionState.mode !== 'legacy' || status === 403) block()
  const retry = error.response?.headers?.['retry-after']
  const wait = /^\d+$/.test(retry || '') ? Number(retry) * 1000 : Math.max(0, Date.parse(retry) - Date.now())
  retryAt = performance.now() + Math.max(Math.min(60000, 10000 * Math.pow(2, failures++)), Number.isFinite(wait) ? wait : 0)
  const serverCode = error.response?.data?.error_code
  const code = invalid ? error.message : (/^[A-Z0-9_]{1,40}$/.test(serverCode || '') ? serverCode : (error.code === 'ECONNABORTED' ? 'REQUEST_TIMEOUT' : (status >= 500 ? 'SESSION_SERVICE_UNAVAILABLE' : 'NETWORK_UNAVAILABLE')))
  reportSessionError(code, null, token, error.response)
}
export async function checkSession (force = false, { allowReturn = false } = {}) {
  const token = current()
  if (!token) return true
  if (window.location.pathname === '/sso/return' && !allowReturn) return false
  if (inFlight) { await inFlight; return sameToken(token) && !sessionState.blocked && failures === 0 }
  if (!force && failures === 0 && checkedAt && performance.now() - checkedAt < 10000 && !sessionState.blocked) return true
  if (performance.now() < retryAt) return false
  const sent = performance.now()
  inFlight = (async () => {
    try {
      const { data } = await api.get('shared-session/status', { headers: headers(token) })
      if (accept(data, token, sent) && data.state !== 'active') await finish(data, token)
    } catch (error) { failed(error, token) }
  })()
  try { await inFlight } finally {
    inFlight = null
    // A real operation may have queued while status was being confirmed.
    if (activityDue()) sendActivity()
    // Diagnostics are optional and must not hold up authorization or real activity.
    if (sameToken(token) && failures === 0) flushEvents(token).catch(() => {})
  }
  return sameToken(token) && !sessionState.blocked && failures === 0
}
export async function requireSession ({ background = false } = {}) {
  if (!current()) return
  // Ordinary API calls use the last valid deadline; SSO entry requires confirmation.
  if (background && failures === 0 && !sessionState.blocked && (sessionState.mode === 'legacy' || (checkedAt && deadline > performance.now()))) {
    checkSession()
    return
  }
  const confirmed = await checkSession()
  if (!confirmed || !current()) {
    const error = new Error('登入狀態尚未確認')
    error.code = 'SESSION_UNCONFIRMED'
    throw error
  }
}
export function recordSharedActivity (event) {
  current()
  if (!event.isTrusted || !credential || sessionState.mode !== 'shared' || sessionState.blocked || document.visibilityState === 'hidden') return
  if (deadline <= performance.now()) { block(); checkSession(true); return }
  activityVersion++
  if (!activityPending) activityPending = uuid()
  if (activityDue()) sendActivity()
}
function activityDue () {
  const remaining = deadline - performance.now()
  // Only queued real activity can bypass throttling in the final ten seconds.
  return activityPending && remaining > 0 &&
    (remaining <= 10000 || performance.now() - lastActivitySent >= 10000)
}
async function sendActivity () {
  const token = current()
  if (!token || sessionState.mode !== 'shared' || !activityPending || inFlight || sessionState.blocked || document.visibilityState === 'hidden' || performance.now() < retryAt) return
  if (deadline <= performance.now()) { block(); checkSession(true); return }
  const id = activityPending
  // Keep the original attempt's version on retry, even if a later operation queued.
  if (pendingActivityVersion === null) pendingActivityVersion = activityVersion
  const version = pendingActivityVersion
  const sent = performance.now()
  lastActivitySent = sent
  inFlight = (async () => {
    try {
      const { data } = await api.post('shared-session/activity', { activity_id: id }, { headers: headers(token) })
      if (accept(data, token, sent)) {
        activityPending = version === activityVersion ? null : uuid()
        pendingActivityVersion = null
        if (data.state !== 'active') await finish(data, token)
      }
    } catch (error) {
      failed(error, token)
      if (error.response?.status === 409) retryAt = 0
    }
  })()
  try { await inFlight } finally {
    inFlight = null
    if (activityDue()) sendActivity()
  }
}
export async function logoutSharedSession () {
  const token = current()
  if (!token) return true
  try {
    await api.post('shared-session/logout', {}, { headers: headers(token) })
  } catch (error) {
    if (error.response?.status !== 401) {
      await Swal.fire({ text: '無法確認兩站登出，請恢復連線後再按登出。', confirmButtonText: '確認' })
      throw error
    }
  }
  if (!sameToken(token)) return false
  clearSession(token)
  return true
}
const OUTBOX = 'sso-event-outbox-v1'
const queue = () => { try { return JSON.parse(localStorage.getItem(OUTBOX) || '[]').filter(e => Date.now() - Date.parse(e.occurred_at) < 86400000).slice(-20) } catch (_) { return [] } }
export function reportSessionError (code, trace = null, token = current(), response = null) {
  if (!token || !sameToken(token) || !sessionState.id) return
  const list = queue()
  const previous = list.find(e => e.session_id === sessionState.id && e.error_code === code && Date.now() - Date.parse(e.occurred_at) < 60000)
  if (!previous) list.push({ event_id: uuid(), event: 'frontend_error', result: 'failure', error_code: code, session_id: sessionState.id, related_request_id: response?.data?.request_id || null, http_status: response?.status || null, ...(trace ? { trace_id: trace } : {}), occurred_at: new Date().toISOString() })
  // Storage quota/privacy errors must never interrupt session cleanup.
  try { localStorage.setItem(OUTBOX, JSON.stringify(list.slice(-20))) } catch (_) {}
}
let nextFlush = 0
let flushFailures = 0
async function flushEvents (token) {
  if (flushing || Date.now() < nextFlush || !sameToken(token)) return
  flushing = true
  try {
    for (const event of queue().filter(e => e.session_id === sessionState.id)) {
      if (!sameToken(token)) break
      try { await api.post('shared-session/events', event, { headers: headers(token) }) } catch (error) {
        if (!error.response || error.response.status === 429 || error.response.status >= 500) {
          const retry = Number(error.response?.headers?.['retry-after']) * 1000 || 0
          nextFlush = Date.now() + Math.max(retry, [10000, 30000, 60000, 300000][Math.min(flushFailures++, 3)])
          break
        }
      }
      localStorage.setItem(OUTBOX, JSON.stringify(queue().filter(e => e.event_id !== event.event_id)))
      flushFailures = nextFlush = 0
    }
  } finally { flushing = false }
}
export async function consumeReturn () {
  if (window.location.pathname !== '/sso/return') return false
  const code = new URLSearchParams(window.location.hash.slice(1)).get('return_code')
  window.history.replaceState(null, '', '/sso/return')
  const original = readToken()
  try {
    if (document.visibilityState === 'hidden') {
      await new Promise(resolve => {
        const visible = () => { if (document.visibilityState !== 'hidden') { document.removeEventListener('visibilitychange', visible); resolve() } }
        document.addEventListener('visibilitychange', visible)
      })
    }
    if (!/^[A-Za-z0-9_-]{43}$/.test(code || '')) throw new Error('RETURN_CODE_INVALID')
    const { data } = await api.post('shared-session/return/consume', { return_code: code })
    if (!data.session_id || !['idle_timeout', 'manual_logout', 'authorization_revoked'].includes(data.reason)) throw new Error('INVALID_RETURN_RESPONSE')
    const stored = readToken()
    // An old return must never clear, or claim a timeout notice for, a newer login.
    if (stored && (!stored.session_id || stored.session_id !== data.session_id || stored.access_token !== original?.access_token)) return true
    const show = data.show_timeout === true && document.visibilityState !== 'hidden'
      ? await claimTimeoutNotice(data, original?.access_token)
      : false
    // Re-check after every await, including a failed notification claim.
    if ((readToken()?.access_token || '') !== (original?.access_token || '')) return true
    if (stored) clearSession(stored.access_token)
    if (show) await Swal.fire({ text: '閒置逾時15分鐘\n請重新登入', customClass: { popup: 'idle-timeout-dialog' }, confirmButtonText: '確認', allowOutsideClick: false })
  } catch (_) {
    // A used/expired return code is not proof that the current login is revoked.
    // Ask the central authority for the same original credential; never clear a newer login.
    if (original?.access_token && sameToken(original.access_token)) {
      await checkSession(true, { allowReturn: true })
      if (!sameToken(original.access_token)) return true
    } else if (readToken()?.access_token) return true
    await Swal.fire({ text: '返回連結已失效或暫時無法驗證，將返回官網首頁。', confirmButtonText: '確認' })
  } finally { window.location.replace('/home') }
  return true
}
export function startSessionMonitor (clear) {
  onClear = clear
  if (started) return
  started = true
  reset()
  const resume = () => { if (document.visibilityState !== 'hidden') { if (current() && sessionState.mode !== 'legacy') block(); checkSession(true) } }
  window.addEventListener('focus', resume)
  window.addEventListener('pageshow', resume)
  window.addEventListener('pagehide', () => { if (current() && sessionState.mode === 'shared') block() })
  window.addEventListener('online', resume)
  window.addEventListener('storage', event => { if (event.key === 'token') { const previous = credential; current(); if (!credential && previous) onClear(); else if (previous !== credential) window.location.reload(); resume() } })
  document.addEventListener('visibilitychange', resume)
  window.setInterval(() => {
    current()
    if (!credential) return
    if (sessionState.mode === 'shared' && deadline <= performance.now()) block()
    if (activityDue()) sendActivity()
  }, 1000)
  window.setInterval(() => {
    if (document.visibilityState !== 'hidden') checkSession(true)
  }, 10000)
  resume()
}
